import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Receipt, Plus, Trash2, FileMinus, AlertCircle, Globe, TrendingDown, Users, Calendar } from 'lucide-react';
import { DateFilter } from '@/components/DateFilter';
import api from '@/lib/api';
import { toast } from 'sonner';
import { localToday } from '@/lib/dateRange';

const CATEGORY_LABELS = {
  services: 'Services / Consulting',
  rent: 'Rent / Lease',
  royalties: 'Royalties / IP',
  dividends: 'Dividends',
  management: 'Management Fees',
  interest: 'Interest',
  insurance: 'Insurance / Reinsurance',
  retention: 'Retention',
  corporate_tax: 'Corporate Tax',
  info: 'Informational',
};

export default function WHTPage() {
  const [rates, setRates] = useState([]);
  const [country, setCountry] = useState('all');
  const [countries, setCountries] = useState([]);
  const [register, setRegister] = useState(null);
  const [dateFilter, setDateFilter] = useState({});
  const [tab, setTab] = useState('rates');

  // Add/edit rate
  const [rateOpen, setRateOpen] = useState(false);
  const [rateForm, setRateForm] = useState({ country_code: 'sa', name: '', rate: 5, category: 'services', description: '' });
  const [editRate, setEditRate] = useState(null);

  // Record WHT payment
  const [payOpen, setPayOpen] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [payForm, setPayForm] = useState({
    supplier_id: '', supplier_name: '', branch_id: '', gross_amount: '',
    rate_id: '', rate: 0, rate_name: '', payment_date: localToday(),
    reference: '', notes: '',
  });

  const fetchRates = async () => {
    try {
      const params = country !== 'all' ? { country_code: country } : {};
      const res = await api.get('/wht/rates', { params });
      setRates(res.data);
    } catch { toast.error('Failed to load WHT rates'); }
  };

  const fetchRegister = async () => {
    try {
      const params = {};
      if (dateFilter.start) params.start_date = dateFilter.start;
      if (dateFilter.end) params.end_date = dateFilter.end;
      const res = await api.get('/wht/register', { params });
      setRegister(res.data);
    } catch { toast.error('Failed to load WHT register'); }
  };

  useEffect(() => {
    api.get('/accounting/tax-config/countries').then(r => setCountries(r.data)).catch(() => {});
    api.get('/suppliers').then(r => setSuppliers(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => { fetchRates(); }, [country]);
  useEffect(() => { fetchRegister(); }, [dateFilter]);

  const saveRate = async () => {
    if (!rateForm.name) { toast.error('Name required'); return; }
    try {
      if (editRate) {
        await api.put(`/wht/rates/${editRate.id}`, rateForm);
        toast.success('Rate updated');
      } else {
        await api.post('/wht/rates', rateForm);
        toast.success('Rate added');
      }
      setRateOpen(false);
      setEditRate(null);
      fetchRates();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const deleteRate = async (id) => {
    if (!window.confirm('Delete this rate?')) return;
    try { await api.delete(`/wht/rates/${id}`); fetchRates(); toast.success('Deleted'); }
    catch { toast.error('Delete failed'); }
  };

  const recordPayment = async () => {
    if (!payForm.gross_amount || !payForm.rate_id) { toast.error('Pick rate + gross amount'); return; }
    try {
      const body = { ...payForm, gross_amount: parseFloat(payForm.gross_amount) };
      const supplier = suppliers.find(s => s.id === payForm.supplier_id);
      if (supplier) body.supplier_name = supplier.name;
      await api.post('/wht/payments', body);
      toast.success(`WHT recorded — withheld ${(parseFloat(payForm.gross_amount) * parseFloat(payForm.rate) / 100).toFixed(2)}`);
      setPayOpen(false);
      setPayForm({
        supplier_id: '', supplier_name: '', branch_id: '', gross_amount: '',
        rate_id: '', rate: 0, rate_name: '', payment_date: localToday(),
        reference: '', notes: '',
      });
      fetchRegister();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const onPickRate = (rid) => {
    const r = rates.find(x => x.id === rid);
    setPayForm({ ...payForm, rate_id: rid, rate: r?.rate || 0, rate_name: r?.name || '' });
  };

  const grossNum = parseFloat(payForm.gross_amount || 0);
  const previewWht = (grossNum * parseFloat(payForm.rate || 0) / 100).toFixed(2);
  const previewNet = (grossNum - previewWht).toFixed(2);

  const flag = (code) => {
    if (!code) return '🌍';
    return code.toUpperCase().replace(/./g, c => String.fromCodePoint(127397 + c.charCodeAt(0)));
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-7xl mx-auto" data-testid="wht-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-rose-500 to-fuchsia-600 flex items-center justify-center shadow-lg">
              <FileMinus size={22} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground">Withholding Tax (WHT)</h1>
              <p className="text-sm text-muted-foreground mt-0.5">
                Multi-country WHT rates & remittance register — KSA, UAE, Bahrain, Oman, Qatar, Kuwait
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={() => setPayOpen(true)} data-testid="record-wht-btn"
              className="bg-rose-500 hover:bg-rose-600 text-white">
              <Plus size={14} className="mr-1" /> Record Withholding
            </Button>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="rates" data-testid="tab-rates">Rate Library</TabsTrigger>
            <TabsTrigger value="register" data-testid="tab-register">WHT Register</TabsTrigger>
          </TabsList>

          <TabsContent value="rates" className="space-y-4">
            <Card>
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <Globe size={16} /> Rate Library
                </CardTitle>
                <div className="flex items-center gap-2">
                  <Select value={country} onValueChange={setCountry}>
                    <SelectTrigger className="w-[180px] h-8 text-xs" data-testid="wht-country-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Countries</SelectItem>
                      {countries.map(c => (
                        <SelectItem key={c.code} value={c.code}>{flag(c.code)} {c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button size="sm" onClick={() => { setEditRate(null); setRateForm({ country_code: 'sa', name: '', rate: 5, category: 'services', description: '' }); setRateOpen(true); }}
                    data-testid="add-wht-rate-btn">
                    <Plus size={12} className="mr-1" /> Add Rate
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50 border-b border-border text-xs text-muted-foreground uppercase">
                      <tr>
                        <th className="text-left px-4 py-2 font-semibold">Country</th>
                        <th className="text-left px-4 py-2 font-semibold">Name</th>
                        <th className="text-left px-4 py-2 font-semibold">Category</th>
                        <th className="text-right px-4 py-2 font-semibold">Rate</th>
                        <th className="text-left px-4 py-2 font-semibold">Description</th>
                        <th className="w-10"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {rates.map(r => (
                        <tr key={r.id} className="border-b border-border hover:bg-muted/30" data-testid={`wht-rate-${r.id}`}>
                          <td className="px-4 py-2 font-medium">
                            <span className="mr-1.5">{flag(r.country_code)}</span>{(r.country_code || '').toUpperCase()}
                          </td>
                          <td className="px-4 py-2 font-medium text-foreground">{r.name}</td>
                          <td className="px-4 py-2 text-xs text-muted-foreground">{CATEGORY_LABELS[r.category] || r.category}</td>
                          <td className="px-4 py-2 text-right font-mono font-semibold text-rose-600">{r.rate}%</td>
                          <td className="px-4 py-2 text-xs text-muted-foreground">{r.description}</td>
                          <td className="px-2 py-2">
                            <button onClick={() => deleteRate(r.id)} className="text-muted-foreground hover:text-error">
                              <Trash2 size={14} />
                            </button>
                          </td>
                        </tr>
                      ))}
                      {rates.length === 0 && (
                        <tr><td colSpan={6} className="text-center py-8 text-muted-foreground text-sm">No rates yet — auto-seed runs on first request.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="register" className="space-y-4">
            <div className="flex items-center justify-end">
              <DateFilter onFilterChange={setDateFilter} />
            </div>
            {register && (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <Card><CardContent className="pt-4">
                    <p className="text-[10px] uppercase text-muted-foreground tracking-wider">Total Gross</p>
                    <p className="text-xl font-bold mt-0.5 font-mono">{(register.summary.total_gross || 0).toLocaleString()}</p>
                  </CardContent></Card>
                  <Card className="border-rose-300 dark:border-rose-700 bg-rose-50/50 dark:bg-rose-900/10">
                    <CardContent className="pt-4">
                    <p className="text-[10px] uppercase text-rose-600 tracking-wider">Total WHT</p>
                    <p className="text-xl font-bold mt-0.5 text-rose-600 font-mono" data-testid="total-wht">{(register.summary.total_wht || 0).toLocaleString()}</p>
                  </CardContent></Card>
                  <Card><CardContent className="pt-4">
                    <p className="text-[10px] uppercase text-muted-foreground tracking-wider">Net Paid Suppliers</p>
                    <p className="text-xl font-bold mt-0.5 font-mono">{(register.summary.total_net_paid || 0).toLocaleString()}</p>
                  </CardContent></Card>
                  <Card className="border-amber-300 dark:border-amber-700">
                    <CardContent className="pt-4">
                    <p className="text-[10px] uppercase text-amber-600 tracking-wider">Pending Remittance</p>
                    <p className="text-xl font-bold mt-0.5 text-amber-600 font-mono">{(register.summary.pending_remittance || 0).toLocaleString()}</p>
                  </CardContent></Card>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <Card>
                    <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Users size={14}/> Top Suppliers</CardTitle></CardHeader>
                    <CardContent className="p-0">
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <tbody>
                            {register.by_supplier_top10.map(s => (
                              <tr key={s.supplier_id} className="border-b border-border">
                                <td className="px-3 py-2 font-medium">{s.supplier_name}</td>
                                <td className="px-3 py-2 text-right text-muted-foreground">{s.count} pmts</td>
                                <td className="px-3 py-2 text-right font-mono text-rose-600">{s.wht_total.toLocaleString()}</td>
                              </tr>
                            ))}
                            {register.by_supplier_top10.length === 0 && <tr><td colSpan={3} className="text-center py-4 text-muted-foreground">No data</td></tr>}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><TrendingDown size={14}/> By Rate</CardTitle></CardHeader>
                    <CardContent className="p-0">
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <tbody>
                            {register.by_rate.map((r, i) => (
                              <tr key={i} className="border-b border-border">
                                <td className="px-3 py-2 font-medium">{r.rate_name}</td>
                                <td className="px-3 py-2 text-right text-muted-foreground">{r.count} × {r.rate}%</td>
                                <td className="px-3 py-2 text-right font-mono text-rose-600">{r.wht_total.toLocaleString()}</td>
                              </tr>
                            ))}
                            {register.by_rate.length === 0 && <tr><td colSpan={3} className="text-center py-4 text-muted-foreground">No data</td></tr>}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                </div>
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Calendar size={14}/> Detailed Log</CardTitle></CardHeader>
                  <CardContent className="p-0 overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-muted/40 text-muted-foreground uppercase">
                        <tr>
                          <th className="text-left px-3 py-2 font-semibold">Date</th>
                          <th className="text-left px-3 py-2 font-semibold">Supplier</th>
                          <th className="text-left px-3 py-2 font-semibold">Rate</th>
                          <th className="text-right px-3 py-2 font-semibold">Gross</th>
                          <th className="text-right px-3 py-2 font-semibold">WHT</th>
                          <th className="text-right px-3 py-2 font-semibold">Net Paid</th>
                          <th className="text-left px-3 py-2 font-semibold">Status</th>
                          <th className="text-right px-3 py-2 font-semibold">Cert</th>
                        </tr>
                      </thead>
                      <tbody>
                        {register.items.slice(0, 100).map(it => (
                          <tr key={it.id} className="border-b border-border">
                            <td className="px-3 py-1.5 font-mono">{it.payment_date}</td>
                            <td className="px-3 py-1.5">{it.supplier_name}</td>
                            <td className="px-3 py-1.5 text-muted-foreground">{it.rate_name} ({it.wht_rate}%)</td>
                            <td className="px-3 py-1.5 text-right font-mono">{(it.gross_amount || 0).toLocaleString()}</td>
                            <td className="px-3 py-1.5 text-right font-mono text-rose-600">{(it.wht_amount || 0).toLocaleString()}</td>
                            <td className="px-3 py-1.5 text-right font-mono">{(it.net_to_supplier || 0).toLocaleString()}</td>
                            <td className="px-3 py-1.5">
                              <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                it.remittance_status === 'remitted' ? 'bg-success/10 text-success' : 'bg-warning/10 text-warning'
                              }`}>
                                {it.remittance_status || 'pending'}
                              </span>
                            </td>
                            <td className="px-3 py-1.5 text-right">
                              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/wht/certificate/${it.id}`}
                                 target="_blank" rel="noreferrer"
                                 className="text-info hover:underline text-[11px] inline-flex items-center gap-1"
                                 data-testid={`wht-cert-${it.id}`}
                                 onClick={async (e) => {
                                   // Use API client to send auth header, then download
                                   e.preventDefault();
                                   try {
                                     const res = await api.get(`/wht/certificate/${it.id}`, { responseType: 'blob' });
                                     const url = URL.createObjectURL(res.data);
                                     const a = document.createElement('a');
                                     a.href = url;
                                     a.download = `WHT_Certificate_${it.supplier_name}_${it.payment_date}.pdf`;
                                     a.click();
                                     URL.revokeObjectURL(url);
                                   } catch { toast.error('Failed to generate certificate'); }
                                 }}>
                                📄 Cert
                              </a>
                            </td>
                          </tr>
                        ))}
                        {register.items.length === 0 && <tr><td colSpan={8} className="text-center py-6 text-muted-foreground">No WHT events recorded.</td></tr>}
                      </tbody>
                    </table>
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>
        </Tabs>

        {/* Record WHT dialog */}
        <Dialog open={payOpen} onOpenChange={setPayOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><FileMinus size={16}/> Record Withholding</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <Label className="text-xs">Supplier</Label>
                <Select value={payForm.supplier_id} onValueChange={(v) => setPayForm({ ...payForm, supplier_id: v })}>
                  <SelectTrigger data-testid="wht-supplier-select"><SelectValue placeholder="Pick supplier" /></SelectTrigger>
                  <SelectContent>
                    {suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">WHT Rate</Label>
                <Select value={payForm.rate_id} onValueChange={onPickRate}>
                  <SelectTrigger data-testid="wht-rate-select"><SelectValue placeholder="Pick rate" /></SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {rates.filter(r => r.rate > 0).map(r => (
                      <SelectItem key={r.id} value={r.id}>{flag(r.country_code)} {r.name} — {r.rate}%</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Gross Amount</Label>
                  <Input type="number" step="0.01" value={payForm.gross_amount}
                    onChange={(e) => setPayForm({ ...payForm, gross_amount: e.target.value })}
                    data-testid="wht-gross-input" />
                </div>
                <div>
                  <Label className="text-xs">Payment Date</Label>
                  <Input type="date" value={payForm.payment_date}
                    onChange={(e) => setPayForm({ ...payForm, payment_date: e.target.value })} />
                </div>
              </div>
              <div>
                <Label className="text-xs">Reference / Invoice #</Label>
                <Input value={payForm.reference} onChange={(e) => setPayForm({ ...payForm, reference: e.target.value })} />
              </div>
              {grossNum > 0 && parseFloat(payForm.rate) > 0 && (
                <div className="rounded-lg bg-rose-50 dark:bg-rose-900/20 border border-rose-200/60 dark:border-rose-800 p-3 space-y-1 text-xs">
                  <div className="flex justify-between"><span>Gross to supplier:</span><span className="font-mono">{grossNum.toFixed(2)}</span></div>
                  <div className="flex justify-between text-rose-600"><span>Withheld @ {payForm.rate}%:</span><span className="font-mono font-semibold">- {previewWht}</span></div>
                  <div className="flex justify-between border-t border-rose-200/60 dark:border-rose-800 pt-1 font-semibold"><span>Net paid to supplier:</span><span className="font-mono">{previewNet}</span></div>
                </div>
              )}
              <div className="flex items-start gap-2 text-[11px] text-muted-foreground">
                <AlertCircle size={12} className="mt-0.5 shrink-0" />
                <span>The WHT amount is held by you and remitted to the tax authority separately. The supplier receives only the net amount.</span>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPayOpen(false)}>Cancel</Button>
              <Button onClick={recordPayment} data-testid="save-wht-btn"
                className="bg-rose-500 hover:bg-rose-600 text-white">Record</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add/Edit rate dialog */}
        <Dialog open={rateOpen} onOpenChange={setRateOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>{editRate ? 'Edit Rate' : 'Add Rate'}</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <Label className="text-xs">Country</Label>
                <Select value={rateForm.country_code} onValueChange={(v) => setRateForm({ ...rateForm, country_code: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {countries.map(c => <SelectItem key={c.code} value={c.code}>{flag(c.code)} {c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Rate Name</Label>
                <Input value={rateForm.name} onChange={(e) => setRateForm({ ...rateForm, name: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Rate %</Label>
                  <Input type="number" step="0.1" value={rateForm.rate}
                    onChange={(e) => setRateForm({ ...rateForm, rate: parseFloat(e.target.value) || 0 })} />
                </div>
                <div>
                  <Label className="text-xs">Category</Label>
                  <Select value={rateForm.category} onValueChange={(v) => setRateForm({ ...rateForm, category: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
                        <SelectItem key={k} value={k}>{v}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs">Description</Label>
                <Input value={rateForm.description} onChange={(e) => setRateForm({ ...rateForm, description: e.target.value })} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRateOpen(false)}>Cancel</Button>
              <Button onClick={saveRate}>{editRate ? 'Update' : 'Create'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
